import { useState, useEffect, useCallback } from 'react';
import { NotificationService, Notification, NotificationStats } from '../services/notificationService';

export interface UseNotificationsReturn {
  notifications: Notification[];
  stats: NotificationStats;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearAll: () => void;
  loading: boolean;
}

export const useNotifications = (): UseNotificationsReturn => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [stats, setStats] = useState<NotificationStats>({ unreadCount: 0, totalCount: 0 });
  const [loading, setLoading] = useState(true);

  // Initialize notification service
  useEffect(() => {
    // Load existing notifications from storage
    NotificationService.loadFromStorage();
    
    // Initialize real-time subscriptions
    NotificationService.initialize();
    
    // Get initial notifications
    setNotifications(NotificationService.getNotifications());
    setStats(NotificationService.getNotificationStats());
    setLoading(false);

    // Subscribe to notification changes
    const unsubscribe = NotificationService.subscribe((newNotifications) => {
      setNotifications(newNotifications);
      setStats(NotificationService.getNotificationStats());
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const markAsRead = useCallback((id: string) => {
    NotificationService.markAsRead(id);
  }, []);

  const markAllAsRead = useCallback(() => {
    NotificationService.markAllAsRead();
  }, []);

  const clearAll = useCallback(() => {
    NotificationService.clearAll();
  }, []);

  return {
    notifications,
    stats,
    markAsRead,
    markAllAsRead,
    clearAll,
    loading
  };
};
